/**
 * ticket.ts — the order ticket pane: types + rendering. No risk math.
 *
 * WHAT THIS IS
 * ------------
 * TradingView's order ticket, as a tab in the existing bottom dock: side
 * (Buy/Sell), order type (Limit/Market), entry price, sizing (explicit size or
 * risk-%), leverage, stop, take-profits, and — the part that matters — the RULE
 * verdicts from the risk constitution printed verbatim next to a Dry-run /
 * Confirm pair.
 *
 * THE ONE RULE, INHERITED FROM main.ts AND tradepanel.ts
 * -----------------------------------------------------
 * This file RENDERS. It computes no risk number and holds no threshold:
 *
 *   * R:R, risk $, reward $, implied size, modelled liquidation → all arrive
 *     from `/api/trade/preview`, which calls `exec_tools.cli.risk_gates`;
 *   * every RULE verdict is printed as the constitution worded it, because those
 *     sentences carry the measured history (the SOL -659% ROE, xyz:MU's +14.61%
 *     earnings bar) and a re-wording would strip the evidence from the warning;
 *   * the submit path is `{verb:'place', symbol, params}` → `/api/trade/plan`,
 *     with argv built server-side from a whitelist.
 *
 * A ticket that computed its own R:R would be the second source of truth about
 * money this repo keeps finding and deleting.
 *
 * WHY A TAB AND NOT A FLOATING PANEL
 * ----------------------------------
 * TradingView floats its ticket over the chart. Here the chart is already
 * `autoSize`d into a grid row (see the CSS note in index.html: an overlay covers
 * candles the chart still believes are visible), and the panel next to it already
 * holds Positions / Orders. Putting the ticket in that same dock means the levels
 * you are dragging and the position they would join are on one screen, which is
 * the reason the panel lives beside a chart at all.
 */
import { fmtPx, fmtSigned, fmtSize, fmtUsd, type Snapshot } from './tradepanel';

/** One gate's outcome, exactly as `trade_ticket._verdict` shapes it.
 *
 *  `ok: null` is a THIRD state and it is load-bearing: the check could not be
 *  evaluated (no equity yet, no card id). Rendering it as a pass would show a
 *  green tick for a check that never ran — which is precisely how six of seven
 *  setups reached the book unchecked on 2026-07-15. */
export type GateVerdict = {
  ok: boolean | null;
  verdict: string | null;
  error: string | null;
  note?: string;
  [k: string]: unknown;
};

export type TicketTarget = {
  price: number;
  rr: number | null;
  distance_pct: number;
  wrong_side: boolean;
};

/** `/api/trade/preview` — ADVISORY. `authoritative` is false by construction. */
export type TicketPreview = {
  symbol: string;
  venue: string;
  side: 'buy' | 'sell';
  direction: 'LONG' | 'SHORT';
  entry: number | null;
  stop: number | null;
  tps: number[];
  leverage: number;
  authoritative: boolean;
  binding_check: string;
  error?: string;
  stop_distance?: number | null;
  stop_distance_pct?: number | null;
  stop_wrong_side?: boolean;
  targets?: TicketTarget[];
  rr?: number | null;
  size?: number | null;
  size_basis?: string;
  notional?: number | null;
  margin?: number | null;
  risk_usd?: number | null;
  reward_usd?: number | null;
  equity?: number | null;
  risk_pct_of_equity?: number | null;
  liq_estimate?: number | null;
  liq_source?: string;
  liq_note?: string;
  /* ---- STUBBED, pending the sizing endpoint (see the block comment below) ---- */
  /** The highest leverage that still keeps the modelled liq beyond the stop. */
  max_leverage?: number | null;
  /** The server's own verdict that liq sits past the stop. Authoritative when present. */
  liq_beyond_stop?: boolean | null;
  /** How far past the stop the liq sits, as a % of entry. Display only. */
  liq_buffer_pct?: number | null;
  /**
   * WHICH PRICE BOUNDS THE LOSS — `'stop'` or `'liquidation'`, from the server's
   * `plan_bracket`. The only way to tell a stopless (liq-bounded) trade from a
   * stop-protected one: in the liq-bounded case `stop` is null and `risk_usd` is
   * the loss AT the liquidation price, not at a stop the user chose. Never infer
   * this from `stop === null` alone — that is also true while the user is still
   * typing.
   */
  risk_bound?: 'stop' | 'liquidation' | null;
  /**
   * WHICH POT `risk_usd` IS 2% OF, in the server's words (card t_e59172b3). Under a
   * unified account it is the whole trading account -- the spot USDC total -- and
   * NOT the clearinghouse of the one dex the symbol trades on, which is a ~1/8 slice
   * of it; a fallback to that slice (non-unified account, unreadable mode or spot
   * state) is named here rather than shown as a silently smaller size. Relayed from
   * `/api/trade/bracket/sized`; nothing here re-derives it.
   */
  equity_basis?: string | null;
  /** What the exchange can actually pull into this order (dex withdrawable + spot
   *  free under a unified account) -- the pot RULE 8 judged the margin against. */
  deployable?: number | null;
  deployable_basis?: string | null;
  account_mode?: string | null;
  gates: Record<string, GateVerdict>;
  warnings: string[];
  blocking?: string[];
  placeable?: boolean;
};

/**
 * STUBBED FIELDS — `max_leverage`, `liq_beyond_stop`, `liq_buffer_pct`.
 *
 * The panel's whole promise is "the system picked the leverage so that the
 * liquidation sits BEYOND your stop". That is a SERVER computation: the sizing
 * endpoint owns the tick/lot rounding, the maintenance-margin table and the
 * equity, and a TypeScript copy of it would be a second source of truth about
 * money that disagrees with the process actually placing the order.
 *
 * So these three arrive from `/api/trade/preview` and are typed optional here.
 * Until the endpoint returns them the panel says "waiting for the server" in the
 * derived rows and the button blocks with that as its stated reason — it does NOT
 * guess. `liqBeyondStop` below falls back to a pure SIGN COMPARISON of two numbers
 * the server already sends (liq_estimate vs stop); that is a display of the
 * server's own outputs, not a re-derivation of them.
 */

/** The ticket's editable state. The single source the chart lines are drawn from. */
export type TicketState = {
  symbol: string;
  side: 'buy' | 'sell';
  type: 'limit' | 'market';
  entry: number | null;
  stop: number | null;
  tps: (number | null)[];
  /** Exactly one of these two is active — mirrors order.py's exclusive group. */
  sizing: 'risk_pct' | 'size';
  risk_pct: number | null;
  size: number | null;
  leverage: number;
  tr_id: string | null;
  /** Set while the ticket is being used to move an EXISTING position's stop/TP. */
  managing: string | null;
  /**
   * WHICH LEG THE USER MOVED. The verb of a manage write is derived from THIS and from
   * nothing else — see `manageIntent`, which is the only place that decision is made.
   *
   * It exists because the decision used to be inferred from STATE: `main.ts` read
   * `st.stop !== null ? 'modify-sl' : 'modify-tp'`, and `ticketFromAdvice` imports BOTH
   * legs off the live position, so `modify-tp` was unreachable whenever a stop was
   * resting. Dragging the green target sent `modify-sl` at the untouched stop price
   * (R4-3, critical): the target did not move, and the stop was cancelled and re-placed
   * for nothing — there is no atomic amend on this venue
   * (`docs/design/HL_TRIGGER_ORDER_SEMANTICS_2026-08-19.md`), so an unprotected window
   * was opened for an operation that changed nothing. Worse than doing nothing.
   *
   * `null` = nothing has been moved yet, which is a REFUSAL rather than a default leg.
   */
  movedLeg: 'stop' | 'tp' | null;
  /**
   * The legs as the EXCHANGE held them when manage mode was entered, so a press that
   * would re-send an unchanged price can be refused instead of paying a cancel+replace
   * for a no-op. Null for a ticket that is not managing anything.
   */
  managedFrom: { stop: number | null; tp: number | null } | null;
};

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

/** A fresh ticket. `TK_DEFAULTS` is the ONE definition of "new", so boot and a market
 *  change cannot disagree about it — that drift is what let a subset be cleared. */
export const TK_DEFAULTS = {
  side: 'buy',
  type: 'limit',
  sizing: 'risk_pct',
  risk_pct: 1,
  size: null,
  leverage: 3,
} as const;

export const freshTicket = (symbol = ''): TicketState => ({
  ...TK_DEFAULTS,
  symbol,
  entry: null,
  stop: null,
  tps: [null, null],
  tr_id: null,
  managing: null,
  movedLeg: null,
  managedFrom: null,
});

/** The ticket state after the chart moves to `symbol`. PURE — the testable core of the
 *  market-change reset, extracted from main.ts so a gate can pin it.
 *
 *  The manage path (`ticketFromAdvice`) imports 8 fields from a live position; clearing only the 4 PRICE
 *  ones left the AMZN short's `side`/`leverage: 10`/`size: 1.648` on a HYPE ticket, which
 *  drew a SHORT's liq (54.40) ABOVE a LONG's 49.458 entry — every number right, wrong
 *  instrument. So leaving manage mode restores defaults WHOLESALE rather than subtracting
 *  a remembered list, while a hand-edited ticket keeps its own side/leverage: those are
 *  the user's preferences, not another market's geometry.
 *  README §"A managed position's SIDE leaked onto the next market". */
export function ticketForSymbol(st: TicketState, symbol: string): TicketState {
  const prefs = st.managing ? TK_DEFAULTS : {};
  return {
    ...st, ...prefs, symbol,
    entry: null, stop: null, tps: [null, null], tr_id: null, managing: null,
    // The moved leg belongs to the position that was being managed, exactly as its
    // prices do: a `movedLeg` surviving onto a fresh market would let a press compose a
    // manage verb for a market the ticket is no longer pointing at.
    movedLeg: null, managedFrom: null,
  };
}

/** The ticket state after a SCAN SETUP is loaded into it. PURE, so a gate can pin it.
 *
 *  Andrei reads a setup off a Telegram card and retypes four prices into this form.
 *  This is the function that kills the retype: the row's own numbers become the
 *  ticket's entry / stop / target and his next action is a DRAG or a PRESS.
 *
 *  A MISSING STOP OR TARGET STAYS MISSING. `/api/setups` sends `sl`/`tp` as null when the
 *  row carries no archived levels (`levels_source: 'not_recorded'` — the row predates
 *  2026-08-09 and no stop was ever archived for that card; the server refuses to re-derive
 *  one with today's rule, card t_7d38d636). The honest ticket for such a row is one with an
 *  entry and an empty stop — which the ticket already
 *  handles (the stopless shape is sized off liquidation). Substituting a guess would
 *  put a price he never chose on the money path wearing the scan's authority.
 *
 *  It is NOT a managed ticket: `managing` marks a ticket that MODIFIES an existing
 *  position, and this is a new entry. Leaving it set would send a modify for a
 *  position that does not exist.
 *
 *  `symbol` is assigned byte for byte. `xyz:AAPL` is a different row from `XYZ:AAPL`
 *  in the table the scan wrote, and normalising here would silently retarget the
 *  order at a market spelled differently from the one advised. */
export function ticketFromSetup(
  st: TicketState,
  setup: { asset: string; direction: string; entry: number | null; sl: number | null; tp: number | null },
): TicketState {
  return {
    ...st,
    // Leaving manage mode restores preferences WHOLESALE, exactly as `ticketForSymbol`
    // does and for the same measured reason: a managed position's side and leverage
    // leaking onto a fresh market drew a short's geometry over a long's entry.
    ...(st.managing ? TK_DEFAULTS : {}),
    symbol: setup.asset,
    side: setup.direction === 'bullish' ? 'buy' : 'sell',
    entry: setup.entry,
    stop: setup.sl,
    tps: [setup.tp, null],
    tr_id: null,
    managing: null,
    // Same reason as `ticketForSymbol`: this is a NEW ENTRY, so there is no leg of an
    // existing position that has been moved and no exchange-held pair to compare against.
    movedLeg: null,
    managedFrom: null,
  };
}

/** What `tkSave` may persist: preferences only, and NOTHING while managing.
 *
 *  Prices are excluded because restoring a stale entry/stop puts levels from a moved book
 *  onto a fresh chart. Manage mode is excluded because saving runs on every drag, so
 *  managing the AMZN short wrote `{"side":"sell","sizing":"size","leverage":10}` and a
 *  reload replayed a short's geometry onto the next market. */
export function ticketPrefs(st: TicketState): Partial<TicketState> | null {
  if (st.managing) return null;
  const { side, type, sizing, risk_pct, leverage } = st;
  return { side, type, sizing, risk_pct, leverage };
}

/** What a press on a MANAGING ticket would send — or why there is nothing to send.
 *
 *  ONE shape, so "refused" and "what would be sent" cannot disagree: a refusal carries
 *  no request, exactly as `livelevels.StagedModify` does for the drag path. Two entry
 *  points to one mutation must not answer this question two different ways. */
export type ManageIntent =
  | { refusal: string; request?: undefined }
  | {
      refusal?: undefined;
      request: { verb: 'modify-sl' | 'modify-tp'; symbol: string; price: number };
    };

/**
 * THE VERB COMES FROM THE LEG THAT WAS MOVED, AND FROM NOTHING ELSE (R4-3, critical).
 *
 * `main.ts::tkPlace` used to decide it from STATE:
 *
 *     const verb = st.managing ? (st.stop !== null ? 'modify-sl' : 'modify-tp') : 'place';
 *     const price = verb === 'modify-sl' ? st.stop : tp1;
 *
 * `ticketFromAdvice` imports BOTH legs off the live position (`stop: advised ??
 * pos.protection.sl`, `tps: [pos.protection.tp, null]`), so `st.stop !== null` holds for
 * every managed position that has a resting stop and `modify-tp` was unreachable by any
 * route a user can take. Dragging the GREEN TARGET therefore sent `modify-sl` at the
 * untouched stop price: the target did not move (silently), and the stop went through a
 * cancel+replace — this venue has no atomic amend
 * (`docs/design/HL_TRIGGER_ORDER_SEMANTICS_2026-08-19.md`) — so the position was left
 * unprotected for a window, to perform an operation that changed nothing.
 *
 * SO THE DECISION IS THE GESTURE'S, RECORDED WHERE THE GESTURE HAPPENS. `tkApplyDrag`
 * and the form's own `input` handler both set `movedLeg`; this function reads it. Two
 * places may no longer decide one question (report §C, "two sources of truth"): there is
 * no state from which the verb can be re-derived, because state cannot distinguish a leg
 * that was dragged from a leg that was merely loaded.
 *
 * A PRESS THAT WOULD CHANGE NOTHING IS REFUSED, not sent. That is the same defect's other
 * half: the harm in R4-3 was not only the wrong verb, it was paying an unprotected window
 * for a no-op. `managedFrom` holds the pair the exchange had when manage mode was entered,
 * so re-sending the resting price is caught here rather than at the venue.
 */
export function manageIntent(st: TicketState): ManageIntent {
  if (!st.managing) return { refusal: 'this ticket is not managing an open position' };
  const leg = st.movedLeg;
  if (leg === null) {
    return {
      refusal: 'drag the red stop line or the green take-profit line first — nothing has '
        + 'been moved, so there is no change to send',
    };
  }
  const price = leg === 'stop' ? st.stop : (st.tps[0] ?? null);
  if (price === null || !Number.isFinite(price) || price <= 0) {
    return {
      refusal: `the ${leg === 'stop' ? 'stop' : 'take profit'} has no usable price`,
    };
  }
  const was = leg === 'stop' ? (st.managedFrom?.stop ?? null) : (st.managedFrom?.tp ?? null);
  if (was !== null && was === price) {
    return {
      refusal: `the ${leg === 'stop' ? 'stop' : 'take profit'} is already at ${price} on `
        + 'the exchange — sending it again would cancel and re-place the same order, '
        + 'leaving the position unprotected for nothing',
    };
  }
  return {
    request: { verb: leg === 'stop' ? 'modify-sl' : 'modify-tp', symbol: st.managing, price },
  };
}

/**
 * APPLY A MOVED LEG, RECORDING WHICH ONE — the production mutator for both the drag and
 * the form (R4-3, critical).
 *
 * It lives HERE, not in `main.ts`, for one reason: `main.ts` is the browser entry point and
 * vitest cannot import it, so a mutator that lived there could only be tested through a
 * COPY of itself in the test file — and a copy would keep passing after the real one was
 * broken. That is the vacuity this repo has paid for repeatedly. `main.ts` now calls this.
 *
 * `movedLeg` is what `manageIntent` reads, and it is set by the GESTURE rather than derived
 * from the resulting state, because state cannot distinguish a leg that was moved from a leg
 * that was merely loaded off the live position.
 *
 * ENTRY AND THE SECOND TARGET RECORD NOTHING. `/api/trade/modify` moves ONE price, and a
 * manage write has no entry leg at all; claiming either as the moved leg would compose a
 * request for something the route cannot express.
 */
export function applyMovedLeg(st: TicketState, leg: string, price: number | null): void {
  if (leg === 'entry') { st.entry = price; return; }
  if (leg === 'stop') {
    st.stop = price;
    st.movedLeg = 'stop';
    return;
  }
  if (leg === 'tp1') {
    st.tps[0] = price;
    st.movedLeg = 'tp';
    return;
  }
  if (leg === 'tp2') { st.tps[1] = price; }
}

/** The chart colours for each leg. Red = loss side, green = profit side, always. */
export const LEG_COLORS = {
  entry: '#2962ff',
  stop: '#ef5350',
  tp: '#26a69a',
  liq: '#ff5252',
} as const;

/** Account equity for the preview's RULE 2/3 denominators.
 *
 *  Reads the SAME field the Balances tab shows (`balances.account_value`), so the
 *  ticket's "risk 1.8% of equity" and the panel's equity cannot disagree. Returns
 *  null rather than 0 when unavailable: the backend then reports those gates as
 *  UNAVAILABLE instead of dividing by a fiction. */
export function equityFrom(snap: Snapshot | null): number | null {
  const v = snap?.balances?.account_value;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Does the modelled liquidation sit BEYOND the stop? The feature's whole claim.
 *
 * Prefers the server's own `liq_beyond_stop` when it is sent (authoritative — it
 * knows the maintenance-margin table). Falls back to comparing two numbers the
 * server already returned: for a long, liq must be BELOW the stop; for a short,
 * ABOVE. That is a sign test on server outputs, not a second sizing model.
 *
 * `null` = not answerable yet. Rendered as such, never as a pass: a green tick for
 * a check that never ran is how six of seven setups reached the book unchecked on
 * 2026-07-15.
 */
export function liqBeyondStop(
  prev: TicketPreview | null,
  side: 'buy' | 'sell',
  stop: number | null,
): boolean | null {
  if (prev?.liq_beyond_stop === true || prev?.liq_beyond_stop === false) {
    return prev.liq_beyond_stop;
  }
  const liq = prev?.liq_estimate;
  if (!liq || !stop || !Number.isFinite(liq) || !Number.isFinite(stop)) return null;
  return side === 'buy' ? liq < stop : liq > stop;
}

/** One thing standing between the user and the trade, in words they can act on. */
export type Blocker = {
  /** Stable id for tests and for `data-blocker`. */
  id: string;
  /** Plain language. No RULE numbers, no field names — an instruction or a fact. */
  text: string;
  /**
   * Does this DISABLE the button, or merely warn beside it?
   *
   * ANDREI, 2026-08-25, verbatim: *"Я готов посмотреть на предупреждения в Web UI,
   * но это не должно быть останавливающим для меня фактором."* So the two kinds are
   * now distinguished in the type rather than by convention, and `advisory: true` is
   * shown and never enforced.
   *
   * WHAT MAY STILL BLOCK, and the test is the same one the card applies to the
   * backend: only things that make the ORDER ITSELF UNDEFINED. No entry price (there
   * is no order to send), writes off (nothing can be sent at all), the wrong chart
   * (the prices describe a different market), nothing to move on an exit. None of
   * those is a risk opinion; each is a missing argument.
   *
   * WHAT MAY NOT, and WHY THAT IS SAFE BY CONSTRUCTION rather than by trust: every
   * advisory item here is a CLIENT-SIDE COPY of a check the write path runs itself.
   * `place` calls the SAME `_derive` -> `exec_tools.sizing.plan_bracket` that RULE 5
   * (stop vs liquidation) and RULE 11 (TP side) live inside, as HARD errors, and
   * `_reject_derived_fields` refuses any client-supplied size or leverage by name. A
   * duplicated refusal in the browser cannot make the trade safer than the server
   * already makes it -- it can only diverge from it, which is the failure mode
   * §0b.1 names. And `/api/trade/preview` says `authoritative: false` in its own
   * payload, so enforcing its verdicts was always the panel over-reading an
   * advisory answer.
   */
  advisory?: boolean;
};

/** The subset that actually disables the button. One definition, so nothing drifts. */
export function blockingOnly(items: Blocker[]): Blocker[] {
  return items.filter((b) => !b.advisory);
}

/**
 * WHY THE BUTTON IS NOT PRESSABLE, in sentences a person can act on.
 *
 * THE DEFECT THIS REPLACES. On 2026-08-05 Andrei pressed the arm button and
 * nothing left the browser. `<button ${missing.length ? 'disabled' : ''}>` with
 * `if (!prev) missing.push('no preview yet')` meant a 503 on an ADVISORY preview
 * silently killed the only control that could place a trade. Two separate faults:
 * an advisory failure was treated as a hard block, and the reason was a phrase
 * ('no preview yet') that told the user nothing to do about it.
 *
 * The rules now:
 *
 *   * only things that genuinely make the ORDER undefined block it — no entry,
 *     no stop, no server sizing, writes off, wrong chart;
 *   * an advisory failure is a WARNING, never a block. The backend refuses unsafe
 *     orders on the write path; the panel must not pre-refuse on a read path;
 *   * every blocker says what to DO. `set a stop` and not `RULE 3/4 refuse a
 *     naked entry`;
 *   * NO TR-CARD REQUIREMENT. A TR-card is a repo research artefact; a chart click
 *     is not a repo workflow, and Andrei confirmed the app does not need one.
 *     Requiring it made the button permanently dead for its actual use.
 *
 * Pure, exported, and unit-tested: this is the function whose emptiness decides
 * whether money can move.
 */
export function ticketBlockers(
  st: TicketState,
  prev: TicketPreview | null,
  opts: {
    tradingEnabled: boolean;
    armReason?: string;
    chartSymbol?: string;
    /** The sizing call's refusal KEYED TO THE ARGUMENTS IT WAS RAISED FOR, or
     *  null/absent while pending. Resolved through `currentSizingError`, so a
     *  refusal about another market or other prices reads as pending rather than
     *  as a confident sentence about a trade that is no longer on screen — see
     *  `sizingFingerprint` and the `sizing-refused` blocker below. */
    sizingRefusal?: SizingRefusal | null;
  },
): Blocker[] {
  const out: Blocker[] = [];
  const sizingError = currentSizingError(st, opts.sizingRefusal);
  if (!opts.tradingEnabled) {
    out.push({
      id: 'writes-off',
      text: `This app cannot place orders right now — ${opts.armReason ?? 'no trading key is loaded'}.`,
    });
  }
  if (opts.chartSymbol && st.symbol && opts.chartSymbol !== st.symbol) {
    out.push({
      id: 'wrong-chart',
      text: `The chart is showing ${opts.chartSymbol} but this trade is for ${st.symbol}. Switch the chart to ${st.symbol}.`,
    });
  }
  if (!st.entry) {
    out.push({ id: 'no-entry', text: 'Set the price you want to get in at — type it above, or drag the blue line on the chart.' });
  }
  if (st.managing) {
    // An EXIT. Moving a stop or a TP needs neither sizing nor a card: `manage.py`
    // gates exits on RULE 5 and a directional RULE 3 only, so demanding either
    // here would block the safest action the panel offers.
    if (st.stop === null && st.tps[0] === null) {
      out.push({ id: 'nothing-to-move', text: 'Drag the red stop line or the green take-profit line to change this position.' });
    }
    return out;
  }
  if (!st.stop) {
    // NO LONGER A BLOCKER. Andrei, 2026-08-07: "without the stop the risk is
    // limited to liquidation level of the isolated margin deal." He is right — in
    // ISOLATED margin the liquidation price bounds the loss, so the server sizes
    // the trade from the liquidation distance instead of a stop distance and the
    // trade is placeable. What replaces the block is DISCLOSURE, not silence: the
    // wipe-out row says liquidation is where this trade ends and what it costs.
    // See `exec_tools/sizing.py` for the derivation and the leverage rule.
    //
    // The branch is kept, empty but for this note, because "why is there no
    // no-stop blocker" is the first question a reader of this function will ask.
    void 0;
  }
  // The server's numbers. Absent, there is nothing to place — but say WHICH way it
  // is absent, because "still working it out" and "it refused" need different acts.
  // Runs with OR without a stop: the stopless shape is sized by the same server
  // call, so the same three failure modes apply to it.
  if (st.entry) {
    if (sizingRefused(sizingError)) {
      // THE SERVER ANSWERED, AND IT SAID NO. This branch comes FIRST because it is
      // the one that was missing: card t_0bd35d10 measured 16 of 16 refusals on
      // this route, and the panel printed the `no-server-numbers` "still waiting"
      // item below for every one of them -- so the user was told to wait for a
      // reply that had already arrived. Ordered above `!prev` because a refused
      // sizing call typically leaves the advisory preview absent too, and "waiting"
      // would win on a naive ordering.
      //
      // ADVISORY, like its neighbours, and for the same reason: the sizing call is
      // a DISPLAY. `place` re-derives every number itself and RULE 5 / RULE 11 are
      // hard errors inside `plan_bracket`, so a display refusal must inform rather
      // than seize the button -- Andrei, 2026-08-25: "I'm ready to look at warnings
      // in the Web UI, but it should not be a stopping factor for me."
      out.push({
        id: 'sizing-refused',
        text: `The server refused to size this trade: ${sizingError}`,
        advisory: true,
      });
    } else if (!prev) {
      // ADVISORY SINCE 2026-08-25, and this is the item Andrei actually hit. The
      // route was taking up to 11 SECONDS (measured in the dev logs:
      // `elapsed_ms=11096.6`, `10945.9`, `9571.4`), and for every one of those
      // seconds the Place button was DEAD with "waiting for the server" underneath
      // it -- the panel was not broken, it was waiting, which is exactly what the
      // screenshot showed. The wait is now ~0.5 s (see `bracket._MARKETS`), but a
      // slow read must never again be able to hold the button hostage: `place`
      // derives every number itself from entry/stop/tp, so the size shown while
      // this is pending is a DISPLAY that is missing, not an argument that is.
      out.push({
        id: 'no-server-numbers',
        text: 'Still waiting for the server to work out the size — the numbers above will fill in. Placing now is fine: the server derives its own size and leverage.',
        advisory: true,
      });
    } else if (prev.error) {
      out.push({
        id: 'server-error',
        text: `The server could not size this trade: ${prev.error}`,
        advisory: true,
      });
    } else if (prev.size === null || prev.size === undefined) {
      out.push({
        id: 'no-size',
        text: 'The server has not worked out a trade size for these prices yet.',
        advisory: true,
      });
    } else if (st.stop && liqBeyondStop(prev, st.side, st.stop) === false) {
      // Only meaningful WITH a stop. With none, liquidation IS the exit and there
      // is no stop for it to arrive before — a check with nothing to compare must
      // not fire, and must not silently "pass" either (see the wipe-out row).
      //
      // ADVISORY, and this is the one that most deserves an explanation. It reads
      // `/api/trade/preview`, which declares `authoritative: false` in its own
      // payload and whose liq buffer is 2% where `hl_check` uses 0.5% -- so the two
      // engines already disagree about this very question by a documented margin.
      // The BINDING answer is RULE 5 inside `plan_bracket`, which `place` cannot
      // avoid: a stop past liquidation raises a hard `SafetyError` there and the
      // order is never signed. Keeping a second, differently-tuned copy as a hard
      // block bought nothing and cost a dead button whenever the advisory number
      // drifted from the binding one.
      out.push({
        id: 'liq-inside-stop',
        text: 'At this leverage you would be wiped out BEFORE your stop is hit. Move the stop further from your entry, or lower the leverage. (The server checks this again and will refuse the order outright if it still holds.)',
        advisory: true,
      });
    }
    if (prev?.blocking?.length) {
      // The constitution's own refusals, verbatim: those sentences carry the
      // measured history and re-wording them would strip the evidence.
      //
      // ADVISORY for the same structural reason: these arrive from the ADVISORY
      // preview, and every rule that governs an entry is re-run inside `place`.
      for (const b of prev.blocking) out.push({ id: 'rule', text: b, advisory: true });
    }
  }
  return out;
}

/** WHERE THIS TICKET'S SETUP SITS IN THE SETUPS LIST, so the next one is a press.
 *
 *  Andrei, 2026-08-18: he must never scroll back to find where he left off. `at` is the
 *  1-based position in the DISPLAYED order (the payload's newest-first order, which the
 *  renderer walks unchanged) and `of` is the list length, so the button labels can say
 *  which of how many he is reading. `hasPrev`/`hasNext` are computed by the controller
 *  against the live list -- edges DISABLE, they never wrap, because wrapping from the
 *  last setup to the first silently restarts a review that looked finished. */
export type ReviewNav = { at: number; of: number; hasPrev: boolean; hasNext: boolean };

/** `[price, price]` band label — e.g. `risk $42.10`. Presentation only. */
function bandLabel(kind: 'risk' | 'reward', usd: number | null | undefined): string {
  if (usd === null || usd === undefined || !Number.isFinite(usd)) return kind;
  return `${kind} ${fmtSigned(kind === 'risk' ? -Math.abs(usd) : Math.abs(usd))}`;
}

/** One verdict row. Colour comes from `ok`, text from the constitution.
 *
 *  `divergence` gets its own line when present: it is the case where the two
 *  audited engines disagree about the same stop, and hiding it would leave the
 *  user reading DEAD_STOP here and CLEAN in the Risk Findings tab with no way to
 *  tell which to believe. */
function gateRow(code: string, v: GateVerdict): string {
  const cls = v.ok === true ? 'pos' : v.ok === false ? 'bad' : 'muted';
  const mark = v.ok === true ? '✓' : v.ok === false ? '✕' : '·';
  const detail = v.ok === false ? v.error : (v.note ?? '');
  const div = typeof v.divergence === 'string'
    ? `<div class="gate-div">⚖ ${esc(v.divergence)}</div>`
    : '';
  return `<tr class="${v.ok === false ? 'gate-bad' : ''}">
    <td class="${cls}">${mark}</td>
    <td><span class="mkt">${esc(code.replace(/_/g, ' '))}</span></td>
    <td class="${cls}">${esc(v.verdict ?? '—')}</td>
    <td class="gate-detail">${esc(detail ?? '')}${div}</td>
  </tr>`;
}

/**
 * The ticket form + live preview.
 *
 * Rendered as one HTML string and re-rendered wholesale (the same approach as
 * `renderTab`), so the caller's delegated listeners keep working and no
 * per-element handler leaks. Inputs carry `data-tf` and are read back by the
 * controller — this function never mutates state.
 */
export function renderTicket(
  st: TicketState,
  prev: TicketPreview | null,
  opts: {
    tradingEnabled: boolean;
    /** WHY writes are off, from the backend, so the note names the real remedy
     *  rather than an env flag that is no longer the gate. */
    armReason?: string;

    equity: number | null;
    busy: boolean;
    /** The market the CHART is showing. When it differs from the ticket's, the
     *  lines are NOT drawn (see `tkDrawLines`) and this must say so — a blank
     *  chart with a populated form otherwise reads as a rendering bug. */
    chartSymbol?: string;
    /** The last place/modify outcome as HTML (`renderTradeRun`), shown under the
     *  button. Cleared by the controller whenever a price changes, so a "placed"
     *  banner can never sit beside prices that describe a different trade. */
    result?: string;
    /** WHERE THIS SETUP SITS IN THE SETUPS LIST, when the ticket was opened from one.
     *  Absent for a hand-built ticket -- there is no sequence to walk. */
    review?: ReviewNav;
    /** THE SIZING CALL'S REFUSAL, KEYED TO THE ARGUMENTS IT WAS RAISED FOR.
     *
     *  `null`/absent = the call has not answered yet (genuinely pending). A
     *  `{key, message}` whose key still matches this state = it answered with a
     *  refusal, and every money row must SHOW that instead of "waiting for the
     *  server" -- see `currentSizingError`. Card t_0bd35d10: the panel spent an
     *  entire session claiming to wait for a reply that had already arrived
     *  sixteen times as a 400; then the first fix let that sentence outlive the
     *  ticket it described, so it is keyed now rather than cleared by hand. */
    sizingRefusal?: SizingRefusal | null;
    /** A coarse-pointer arm is live for these prices (`placearm.isArmed`): the button reads
     *  "Tap again to place". FR3.3, intent 261018-mobile-v1 (2026-10-18). */
    armed?: boolean;
    /** A touch-first device (`legdrag.coarsePointer`): placing takes two taps (FR3.3), so the
     *  ready line says so. Absent = a fine pointer, one press. */
    coarse?: boolean;
  },
): string {

  const sideBtn = (side: 'buy' | 'sell', label: string) =>
    `<button class="tk-side ${side} ${st.side === side ? 'on' : ''}" data-tk="side" data-val="${side}">${label}</button>`;
  const typeBtn = (t: 'limit' | 'market', label: string) =>
    `<button class="tk-type ${st.type === t ? 'on' : ''}" data-tk="type" data-val="${t}">${label}</button>`;

  const numField = (
    name: string,
    label: string,
    value: number | null,
    extra = '',
    wide = false,
  ) => `<label class="tk-f${wide ? ' tk-wide' : ''}"><span>${label}</span>
      <input type="number" step="any" inputmode="decimal" data-tk="${name}" value="${value ?? ''}" />
      ${extra}</label>`;

  const g = prev?.gates ?? {};
  const gateRows = Object.entries(g)
    .map(([code, v]) => gateRow(code, v))
    .join('');

  // The chart is showing a DIFFERENT market, so the levels are deliberately not
  // drawn. Said out loud: an empty chart beside a filled-in form would otherwise
  // read as a broken renderer rather than a refusal.
  const offChart =
    opts.chartSymbol && st.symbol && opts.chartSymbol !== st.symbol
      ? `<div class="tp-note warn">Your three lines are NOT on the chart: it is showing
           <b>${esc(opts.chartSymbol)}</b> while this trade is for
           <b>${esc(st.symbol)}</b>. Switch the chart to ${esc(st.symbol)}.</div>`
      : '';


  // THE DERIVED BLOCK. Everything here is the SERVER's answer, shown in the words
  // Andrei uses rather than the words the risk engine uses ("what you will lose if
  // the stop is hit", not "risk_usd"). Nothing here is typeable, and nothing here
  // is computed in this file — see the STUBBED FIELDS note above.
  const derived = (label: string, value: string, note = '', cls = '') =>
    `<div class="tk-d ${cls}"><span class="tk-d-l">${label}</span>
       <span class="tk-d-v">${value}</span>
       ${note ? `<span class="tk-d-n">${note}</span>` : ''}</div>`;

  // THE REFUSAL, ONLY IF IT STILL DESCRIBES THIS TICKET. A stored sentence about
  // another market or other prices is resolved to null here (= pending), so it is
  // not representable for it to outlive the state it explains.
  const sizingError = currentSizingError(st, opts.sizingRefusal);
  const pending = pendingOrReason(sizingError);
  const lev = prev?.max_leverage ?? prev?.leverage ?? null;
  const beyond = liqBeyondStop(prev, st.side, st.stop);
  // NO STOP: the loss is bounded by the LIQUIDATION price instead, and the server
  // says so with `risk_bound`. Read from the server, never inferred from an empty
  // stop field — that is also empty while the user is mid-type, and claiming a
  // liquidation-bounded trade before the server has sized one would show numbers
  // nothing will honour.
  const liqBound = prev?.risk_bound === 'liquidation';

  // The statement the whole feature exists to make. FOUR states now, and neither
  // the unknown one nor the stopless one is dressed as a pass.
  const liqLine = liqBound
    ? derived(
        'This trade ends here',
        fmtPx(prev?.liq_estimate ?? null),
        `You set no stop, so this is your exit: if the price reaches it your position
         is closed for you and you lose
         ${prev?.risk_usd === null || prev?.risk_usd === undefined
           ? 'the cash held as margin'
           : `<b>${fmtSigned(-Math.abs(prev.risk_usd))}</b>`}.
         Nothing gets you out before it.`,
        'warn',
      )
    : beyond === true
      ? derived(
          'Wipe-out price',
          fmtPx(prev?.liq_estimate ?? null),
          `safely past your stop of ${fmtPx(st.stop)} — your stop is hit first`,
          'pos',
        )
      : beyond === false
        ? derived(
            'Wipe-out price',
            fmtPx(prev?.liq_estimate ?? null),
            `DANGER: this is reached BEFORE your stop of ${fmtPx(st.stop)}`,
            'bad',
          )
        : derived('Wipe-out price', pending, 'needs an entry and a stop', 'muted');

  // The loss $ and the R:R, named once: the derived rows and the `.tk-commit` line beside
  // the Place button (FR3.2, intent 261018-mobile-v1) print the SAME strings, so the phone's
  // one-line summary cannot read a different number from the rows above it.
  const riskText =
    prev?.risk_usd === null || prev?.risk_usd === undefined
      ? pending
      : `<b>${fmtSigned(-Math.abs(prev.risk_usd))}</b>`;
  const rrText = prev?.rr === null || prev?.rr === undefined ? '' : `${prev.rr.toFixed(2)}x what you risk`;

  const derivedRows = [
    derived(
      liqBound ? 'You will lose if it goes against you' : 'You will lose if the stop is hit',
      riskText,
      prev?.risk_pct_of_equity === null || prev?.risk_pct_of_equity === undefined
        ? ''
        : `${prev.risk_pct_of_equity}% of your account`,
    ),
    derived(
      'You will make if the target is hit',
      prev?.reward_usd === null || prev?.reward_usd === undefined ? pending : fmtSigned(prev.reward_usd),
      rrText,
    ),
    derived(
      'How much you will buy',
      prev?.size === null || prev?.size === undefined ? pending : fmtSize(prev.size),
      prev?.notional === null || prev?.notional === undefined ? '' : `${fmtUsd(prev.notional)} position`,
    ),
    derived(
      'Leverage the system chose',
      lev === null ? pending : `<b>${lev}x</b>`,
      liqBound
        ? 'the most this market allows, because with no stop nothing else limits it'
        : 'picked from your stop distance, not typed',
    ),
    derived(
      'Cash held as margin',
      prev?.margin === null || prev?.margin === undefined ? pending : fmtUsd(prev.margin),
      // WHAT CAN ACTUALLY BE DEPLOYED, beside the margin this order needs (card
      // t_e59172b3). The server's RULE 8 refused the order if margin exceeded it;
      // here it is shown so the user sees how much room is left.
      prev?.deployable === null || prev?.deployable === undefined
        ? ''
        : `of ${fmtUsd(prev.deployable)} you can deploy`,
    ),
    // WHICH POT THE 2% IS OF, in the server's own words (card t_e59172b3). Andrei's
    // "маржа примерно в 10 раз меньше ожидаемой" was a size computed from the one
    // dex's slice of his account with nothing on screen saying so. The sentence is
    // the server's (`risk_gates.hl_equity_bases`), relayed verbatim, and it is
    // rendered ONLY when the server sent it: a ticket with no basis says nothing,
    // never a guess at one.
    prev?.equity_basis
      ? derived(
          'Sized against',
          prev?.equity === null || prev?.equity === undefined ? pending : fmtUsd(prev.equity),
          esc(prev.equity_basis),
          prev.equity_basis.startsWith('whole account') ? '' : 'warn',
        )
      : '',
    liqLine,
  ].join('');

  const warn = (prev?.warnings ?? [])
    .map((w) => `<div class="tp-note bad">! ${esc(w)}</div>`)
    .join('');

  // An ADVISORY preview that failed is a warning, never a block — the whole point
  // of the fix. Said out loud so the user knows the checks are not shown, rather
  // than wondering why the verdict table is empty.
  const adviceDown =
    prev === null && st.entry && st.stop
      ? `<div class="tp-note warn">The safety checks could not be read just now. The
           server still runs them when you place the trade — it will refuse an
           unsafe order.</div>`
      : '';

  const blockers = ticketBlockers(st, prev, opts);
  // TWO LISTS FROM ONE CALL. `blockers` is everything the user should SEE;
  // `blocking` is the subset that may disable the button. Derived through
  // `blockingOnly` rather than re-filtered here, so the button and the tests cannot
  // come to disagree about which items are enforced.
  const blocking = blockingOnly(blockers);
  const blockerList = blockers.length
    ? `<ul class="tk-blockers">${blockers
        .map(
          (b) =>
            `<li data-blocker="${esc(b.id)}"${b.advisory ? ' data-advisory="1"' : ''}>${esc(b.text)}</li>`,
        )
        .join('')}</ul>`
    : '';

  const managing = Boolean(st.managing);
  const label = managing
    ? 'Update this position'
    : `Place ${st.side === 'buy' ? 'buy' : 'sell'} trade`;
  // TWO TAPS ON A COARSE POINTER (FR3.3, intent 261018-mobile-v1, 2026-10-18). Since c0e36402
  // ("one-button bracket entry") one press of `#tk-place` places. On a phone the first tap only
  // arms (`placearm.ts`, decided in `main.ts::tkPlaceTap`) and `opts.armed` swaps `label` for
  // "Tap again to place". A fine pointer is never armed, so desktop keeps the one press.
  // THE READY LINE COUNTS THE SAME TAPS (t_1ea62600, 2026-10-18, U3 review R-01): it said
  // "One press places the trade" on the phone too, where one press only arms.
  const readyLine = opts.coarse
    ? 'Ready. Two taps place the trade and its stop and target together.'
    : 'Ready. One press places the trade and its stop and target together.';

  // THE REVIEW WALK. Only rendered when the ticket came from a setup row: a hand-built
  // ticket has no place in the list, and a `1 of 87` next to one would be a lie.
  // `Back to setups` returns to the row that opened this one -- the controller keeps the
  // cursor, so returning is not a scroll hunt.
  const review = opts.review
    ? `<div class="tk-row tk-review">
         <button class="mini" data-tk="review-prev" ${opts.review.hasPrev ? '' : 'disabled'}
                 title="the setup above this one in the list">&lt; Newer setup</button>
         <span class="muted">setup ${opts.review.at} of ${opts.review.of}</span>
         <button class="mini" data-tk="review-next" ${opts.review.hasNext ? '' : 'disabled'}
                 title="the setup below this one in the list">Older setup &gt;</button>
         <button class="mini" data-tk="go-setups" title="back to the list, at this setup">Back to setups</button>
       </div>`
    : '';

  return `
  <div class="tk-wrap">
    <div class="tk-form">
      ${review}
      ${managing
        ? `<div class="tk-row tk-managing">CHANGING YOUR OPEN <b>${esc(st.symbol)}</b> TRADE —
             drag the red stop or green target line, then press the button
             <button class="mini" data-tk="new-order" title="discard and set up a NEW trade instead">new trade</button>
             <button class="mini" data-tk="go-positions" title="back to your open trades">&lt;- My trades</button>
           </div>`
        : `<div class="tk-row tk-nav">
             <span class="muted">Set three prices. Everything else is worked out for you.</span>
             <button class="mini" data-tk="go-positions" title="back to your open trades">&lt;- My trades</button>
             <button class="mini" data-tk="go-orders" title="see waiting orders">Waiting orders</button>
           </div>`}

      <div class="tk-row tk-sides">${sideBtn('buy', 'Buy / Long')}${sideBtn('sell', 'Sell / Short')}</div>

      <div class="tk-row">${typeBtn('limit', 'Wait for my price')}${typeBtn('market', 'Buy now at market')}
        <button class="mini" data-tk="focus" title="zoom the chart so your three lines are readable">focus</button>
        <span class="muted tk-sym">${esc(st.symbol || '—')}</span></div>

      ${numField('entry', 'Get in at', st.entry,
        '<button class="mini" data-tk="entry-mark" title="use the current price">now</button>')}
      ${numField('stop', 'Get out if wrong (stop)', st.stop)}
      ${numField('tp1', 'Take profit at', st.tps[0] ?? null)}
    </div>

    <div class="tk-preview">
      ${offChart}
      ${warn}
      ${adviceDown}

      <div class="tk-derived">${derivedRows}</div>

      ${gateRows
        ? `<details class="tk-gates-wrap"><summary class="muted">Safety checks (${
            Object.keys(g).length
          })</summary>
           <table class="tp-table tk-gates"><thead><tr><th></th><th>Gate</th><th>Verdict</th>
             <th>The constitution's own words</th></tr></thead><tbody>${gateRows}</tbody></table></details>`
        : ''}

      <div class="tk-actions">
        <div class="tk-commit">Lose ${riskText} · make ${rrText || pending}</div>
        ${blockerList || `<div class="tk-ready pos">${readyLine}</div>`}
        <button id="tk-place" ${blocking.length || opts.busy ? 'disabled' : ''}>${
          opts.busy ? 'Placing…' : opts.armed ? 'Tap again to place' : esc(label)
        }</button>
      </div>
      ${opts.result ?? ''}
    </div>
  </div>`;
}

/**
 * The panel's answer when the SIZING call refused, rather than a silent wait.
 *
 * ORIGIN: card t_0bd35d10, 2026-08-27. Andrei's ORDER TICKET printed
 * "waiting for the server" in every derived row, and it had not been waiting: 16
 * of 16 `POST /api/trade/bracket/sized` calls in the dev log were 400s. The reply
 * had ARRIVED, carried a sentence naming the cause, and `tkFetchPreview` dropped
 * it on the floor -- `catch { tkSized = null; }` -- which is indistinguishable in
 * the rendered panel from a call still in flight. A pending state shown for a
 * refusal is worse than an error, because it invites waiting for something that
 * will never come.
 *
 * TWO STATES, NEVER ONE. `sizingError == null` still means pending (honest: the
 * numbers really are on their way), and a non-empty string means REFUSED with the
 * server's own sentence. `sizingRefused` is what the renderer asks, so the two can
 * never be conflated by a truthiness test on a string that may be empty.
 *
 * THE SERVER'S WORDS, VERBATIM. These sentences carry the measured history the
 * repo's §0b.1 exists to protect -- `_explain_refusal`'s zero-balance note names
 * the HIP-3 sub-dex the money is actually on, which is the fact that turns "the
 * app is broken" into "transfer collateral or trade an xyz symbol". Re-wording
 * would strip exactly the actionable half.
 */
export function sizingRefused(sizingError: string | null | undefined): boolean {
  return typeof sizingError === 'string' && sizingError.trim() !== '';
}

/**
 * A refusal held with the ARGUMENTS it was raised for. See `sizingFingerprint`.
 */
export type SizingRefusal = { key: string; message: string };

/**
 * THE EXACT ARGUMENTS `/api/trade/bracket/sized` WAS ASKED ABOUT, as one string.
 *
 * ORIGIN: review round 1 of card t_0bd35d10 (redteam, F1). The first fix stored the
 * refusal in a bare `string | null` and cleared it at two sites. `tkSyncSymbol`
 * replaces the ticket's market and prices (`ticketForSymbol`) and was not one of
 * them, so switching the asset dropdown after a refusal left SIX money rows on a
 * SOL ticket holding no prices reading "the server refused: ... equity for BTC is
 * read from the main perp dex ...". That is strictly worse than the
 * "waiting for the server" it replaced: uninformative became confidently wrong
 * about another market — the same failure `tkDrawLines` refuses to commit with
 * levels.
 *
 * A THIRD CLEAR SITE WOULD BE THE SAME BUG WITH A LONGER LIST. There are four
 * writers of the ticket's prices today (`tkSyncSymbol`, `ticketFromSetup`,
 * `ticketFromAdvice`, the form/drag handlers) and nothing stops a fifth. So the
 * refusal is KEYED instead: the renderer is handed the message only when the
 * stored key still equals the CURRENT state's, and a mismatch renders as pending.
 * Staleness then cannot be reached by forgetting anything — it is not
 * representable.
 *
 * It fingerprints exactly the fields `tkFetchPreview` sends and no others
 * (`symbol`, `side`, `entry`, `stop`, first `tp`): a refusal about these prices is
 * still true while leverage or risk-% change, and re-asking would flash a pending
 * state for nothing. `null` prices are spelled out rather than coerced — `Number('')`
 * is 0 and a stop of zero is a WRONG stop, not a missing one.
 */
export function sizingFingerprint(st: TicketState): string {
  const px = (v: number | null | undefined): string =>
    typeof v === 'number' && Number.isFinite(v) ? String(v) : 'none';
  return [st.symbol || 'none', st.side, px(st.entry), px(st.stop), px(st.tps[0])].join('|');
}

/**
 * The refusal sentence IF it still describes the ticket on screen, else null.
 *
 * ONE function, so the renderer and the blocker list cannot disagree about whether
 * a stored refusal is current. A mismatch is PENDING, not an error: the call for
 * the new arguments really is on its way.
 */
export function currentSizingError(
  st: TicketState,
  held: SizingRefusal | null | undefined,
): string | null {
  if (!held || !sizingRefused(held.message)) return null;
  return held.key === sizingFingerprint(st) ? held.message : null;
}

/**
 * What a derived row shows when there is no number: the reason, or "pending".
 *
 * ONE function so the six money rows cannot disagree about it. Returns HTML
 * because that is what the rows interpolate; the message is escaped here, at the
 * boundary, so no caller can forget.
 */
export function pendingOrReason(sizingError: string | null | undefined): string {
  return sizingRefused(sizingError)
    ? `<span class="bad" data-tk-refused="1">the server refused: ${esc(sizingError)}</span>`
    : '<span class="muted">waiting for the server</span>';
}

/** The chart geometry for a ticket state. Pure translation, no rules.
 *
 *  Bands are only emitted when both edges exist AND sit on the correct side of
 *  entry: a green "reward" band drawn below a long's entry would render an
 *  invalid ticket as a normal-looking trade, which is the class of confidently-
 *  wrong picture this app exists to avoid. The warning still names the problem. */
export function ticketGeometry(st: TicketState, prev: TicketPreview | null) {
  const legs = [];
  const bands = [];
  const isLong = st.side === 'buy';
  if (st.entry) {
    legs.push({
      id: 'entry',
      kind: 'entry' as const,
      price: st.entry,
      label: `${isLong ? 'BUY' : 'SELL'} ${st.type} ${fmtPx(st.entry)}`,
      color: LEG_COLORS.entry,
      dashed: st.type === 'limit',
      draggable: true,
    });
  }
  if (st.stop) {
    legs.push({
      id: 'stop',
      kind: 'stop' as const,
      price: st.stop,
      label: `SL ${fmtPx(st.stop)}${
        prev?.risk_usd === null || prev?.risk_usd === undefined ? '' : `  ${fmtSigned(-Math.abs(prev.risk_usd))}`
      }`,
      color: LEG_COLORS.stop,
      dashed: true,
      draggable: true,
    });
  }
  st.tps.forEach((tp, i) => {
    if (!tp) return;
    const rr = prev?.targets?.[i]?.rr;
    legs.push({
      id: `tp${i + 1}`,
      kind: 'tp' as const,
      price: tp,
      label: `TP${i + 1} ${fmtPx(tp)}${rr === null || rr === undefined ? '' : `  ${rr.toFixed(2)}R`}`,
      color: LEG_COLORS.tp,
      dashed: true,
      draggable: true,
    });
  });
  // The modelled liquidation, drawn but NOT draggable: it is a consequence of
  // leverage, not an input. Seeing how close it sits to the stop is RULE 5 made
  // visual.
  if (prev?.liq_estimate) {
    legs.push({
      id: 'liq',
      kind: 'liq' as const,
      price: prev.liq_estimate,
      label: `LIQ ~${fmtPx(prev.liq_estimate)} (modelled)`,
      color: LEG_COLORS.liq,
      dashed: true,
      draggable: false,
    });
  }
  if (st.entry && st.stop && (isLong ? st.stop < st.entry : st.stop > st.entry)) {
    bands.push({
      from: st.entry,
      to: st.stop,
      fill: 'rgba(239, 83, 80, 0.13)',
      label: bandLabel('risk', prev?.risk_usd),
      labelColor: 'rgba(239, 83, 80, 0.85)',
    });
  }
  const tp1 = st.tps[0];
  if (st.entry && tp1 && (isLong ? tp1 > st.entry : tp1 < st.entry)) {
    bands.push({
      from: st.entry,
      to: tp1,
      fill: 'rgba(38, 166, 154, 0.13)',
      label: bandLabel('reward', prev?.reward_usd),
      labelColor: 'rgba(38, 166, 154, 0.85)',
    });
  }
  return { legs, bands };
}
